import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

import API from "../../utils/apiCalling";
import { config } from "../../utils/apiUrl";

/*
|--------------------------------------------------------------------------
| TENANT API
|--------------------------------------------------------------------------
|
| Default API instance uses:
|
| localStorage.accessToken
|
| This is correct for TENANT_USER authentication.
|--------------------------------------------------------------------------
*/

const api = new API();

/*
|--------------------------------------------------------------------------
| RESPONSE HELPER
|--------------------------------------------------------------------------
*/

const getResponseData = (response) => {
  return response?.data ?? response ?? null;
};

/*
|--------------------------------------------------------------------------
| ERROR HELPER
|--------------------------------------------------------------------------
*/

const getErrorPayload = (error, fallbackMessage) => {
  return {
    message: error?.message || fallbackMessage,

    errorCode: error?.errorCode || error?.code || null,

    statusCode: error?.statusCode || error?.status || null,

    details: error?.details || null,
  };
};

/*
|--------------------------------------------------------------------------
| NORMALIZE PERMISSION
|--------------------------------------------------------------------------
*/

/*
|--------------------------------------------------------------------------
| NORMALIZE PERMISSION
|--------------------------------------------------------------------------
|
| Supports:
|
| "employee.leave.view"
|
| and:
|
| {
|   code: "employee.leave.view"
| }
|
|--------------------------------------------------------------------------
*/

const normalizePermission = (value) => {
  if (!value) {
    return "";
  }

  /*
  |--------------------------------------------------------------------------
  | PERMISSION OBJECT
  |--------------------------------------------------------------------------
  */

  if (typeof value === "object") {
    return String(value?.code || "")
      .trim()
      .toLowerCase();
  }

  /*
  |--------------------------------------------------------------------------
  | PERMISSION CODE STRING
  |--------------------------------------------------------------------------
  */

  return String(value).trim().toLowerCase();
};

/*
|--------------------------------------------------------------------------
| FETCH TENANT PROFILE
|--------------------------------------------------------------------------
|
| GET /v1/admin/auth/me
|
|--------------------------------------------------------------------------
*/

export const fetchTenantProfile = createAsyncThunk(
  "tenantProfile/fetchTenantProfile",

  async (_, { rejectWithValue }) => {
    try {
      /*
        |--------------------------------------------------------------------------
        | API
        |--------------------------------------------------------------------------
        */

      const response = await api.get(config.getTenantProfile);

      /*
        |--------------------------------------------------------------------------
        | API HELPER ERROR RESPONSE
        |--------------------------------------------------------------------------
        */

      if (response?.success === false) {
        return rejectWithValue({
          message: response?.message || "Unable to load business profile.",

          errorCode: response?.errorCode || null,

          statusCode: response?.code || response?.statusCode || null,

          details: response?.details || null,
        });
      }

      /*
        |--------------------------------------------------------------------------
        | RESPONSE DATA
        |--------------------------------------------------------------------------
        */

      const data = getResponseData(response);

      /*
        |--------------------------------------------------------------------------
        | EFFECTIVE PERMISSION CODES
        |--------------------------------------------------------------------------
        */

      const permissions = Array.isArray(data?.permissions)
        ? [
            ...new Set(
              data.permissions.map(normalizePermission).filter(Boolean),
            ),
          ]
        : [];

      /*
        |--------------------------------------------------------------------------
        | PERMISSION DETAILS
        |--------------------------------------------------------------------------
        */

      const permissionDetails = Array.isArray(data?.permissionDetails)
        ? data.permissionDetails
        : [];

      /*
        |--------------------------------------------------------------------------
        | RESULT
        |--------------------------------------------------------------------------
        */

      return {
        user: data?.user || null,

        role: data?.role || null,

        tenant: data?.tenant || null,

        permissions,

        permissionDetails,

        raw: data,
      };
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to load business profile."),
      );
    }
  },
);

/*
|--------------------------------------------------------------------------
| UPDATE TENANT PROFILE
|--------------------------------------------------------------------------
*/

export const updateTenantProfile = createAsyncThunk(
  "tenantProfile/updateTenantProfile",

  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(
        config.updateTenantProfile,

        payload,
      );

      /*
        |--------------------------------------------------------------------------
        | API ERROR RESPONSE
        |--------------------------------------------------------------------------
        */

      if (response?.success === false) {
        return rejectWithValue({
          message: response?.message || "Unable to update profile.",

          errorCode: response?.errorCode || null,

          statusCode: response?.code || null,
        });
      }

      return response?.data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to update profile."),
      );
    }
  },
);

/*
|--------------------------------------------------------------------------
| UPDATE TENANT BUSINESS PROFILE
|--------------------------------------------------------------------------
*/

export const updateTenantBusinessProfile = createAsyncThunk(
  "tenantProfile/updateTenantBusinessProfile",

  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(
        config.updateTenantBusinessProfile,

        payload,
      );

      if (response?.success === false) {
        return rejectWithValue({
          message: response?.message || "Unable to update business details.",

          errorCode: response?.errorCode || null,

          statusCode: response?.code || null,
        });
      }

      return response?.data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to update business details."),
      );
    }
  },
);

/*
|--------------------------------------------------------------------------
| CHANGE TENANT PASSWORD
|--------------------------------------------------------------------------
*/

export const changeTenantPassword = createAsyncThunk(
  "tenantProfile/changeTenantPassword",

  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(
        config.changeTenantPassword,

        payload,
      );

      if (response?.success === false) {
        return rejectWithValue({
          message: response?.message || "Unable to change password.",

          errorCode: response?.errorCode || null,

          statusCode: response?.code || null,
        });
      }

      return response;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to change password."),
      );
    }
  },
);

/*
|--------------------------------------------------------------------------
| UPDATE BASIC SETTINGS
|--------------------------------------------------------------------------
*/

export const updateTenantBasicSettings = createAsyncThunk(
  "tenantProfile/updateTenantBasicSettings",

  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.put(
        config.updateTenantBasicSettings,

        payload,
      );

      if (response?.success === false) {
        return rejectWithValue({
          message: response?.message || "Unable to update basic settings.",

          errorCode: response?.errorCode || null,

          statusCode: response?.code || null,
        });
      }

      return response?.data || null;
    } catch (error) {
      return rejectWithValue(
        getErrorPayload(error, "Unable to update basic settings."),
      );
    }
  },
);

/*
|--------------------------------------------------------------------------
| UPDATE DEFAULT INVOICE TEMPLATE
|--------------------------------------------------------------------------
*/

export const updateTenantInvoiceTemplate = createAsyncThunk(
  "tenantProfile/updateTenantInvoiceTemplate",

  async (
    invoiceTemplate,
    {
      rejectWithValue,
    }
  ) => {
    try {
      const response =
        await api.put(
          config
            .updateTenantInvoiceTemplate,

          {
            invoiceTemplate,
          }
        );

      if (
        response?.success ===
        false
      ) {
        return rejectWithValue({
          message:
            response?.message ||
            "Unable to update default invoice template.",

          errorCode:
            response?.errorCode ||
            null,

          statusCode:
            response?.code ||
            null,
        });
      }

      return response?.data ||
        null;
    } catch (
      error
    ) {
      return rejectWithValue(
        getErrorPayload(
          error,
          "Unable to update default invoice template."
        )
      );
    }
  }
);

/*
|--------------------------------------------------------------------------
| UPDATE INVOICE BRANDING
|--------------------------------------------------------------------------
*/

export const updateTenantInvoiceBranding = createAsyncThunk(
  "tenantProfile/updateTenantInvoiceBranding",

  async (
    payload,
    {
      rejectWithValue,
    }
  ) => {

    try {

      const response =
        await api.put(
          config
            .updateTenantInvoiceBranding,

          payload
        );


      if (
        response?.success ===
        false
      ) {

        return rejectWithValue({

          message:
            response?.message ||
            "Unable to update invoice branding.",

          errorCode:
            response?.errorCode ||
            null,

          statusCode:
            response?.code ||
            response?.statusCode ||
            null,

        });

      }


      return response?.data ||
        null;

    } catch (
      error
    ) {

      return rejectWithValue(
        getErrorPayload(
          error,
          "Unable to update invoice branding."
        )
      );

    }

  }
);


/*
|--------------------------------------------------------------------------
| INITIAL STATE
|--------------------------------------------------------------------------
*/
const initialState = {
  /*
  |--------------------------------------------------------------------------
  | PROFILE
  |--------------------------------------------------------------------------
  */

  user: null,

  role: null,

  tenant: null,

  /*
  |--------------------------------------------------------------------------
  | EFFECTIVE PERMISSIONS
  |--------------------------------------------------------------------------
  |
  | permissions:
  |
  | [
  |   "employee.leave.view",
  |   "employee.salary.view"
  | ]
  |
  | permissionDetails:
  |
  | [
  |   {
  |     code: "employee.leave.view",
  |     module: "EMPLOYEE",
  |     group: "EMPLOYEE_LEAVE",
  |     action: "VIEW"
  |   }
  | ]
  |
  |--------------------------------------------------------------------------
  */

  permissions: [],

  permissionDetails: [],

  /*
  |--------------------------------------------------------------------------
  | STATE
  |--------------------------------------------------------------------------
  */

  loading: false,

  initialized: false,

  error: null,

  lastFetchedAt: null,

  updating: false,

  updateError: null,

  updateSuccess: false,

  businessUpdating: false,

  businessUpdateError: null,

  businessUpdateSuccess: false,

  passwordUpdating: false,

  passwordUpdateError: null,

  passwordUpdateSuccess: false,

  basicSettingsUpdating: false,

  basicSettingsUpdateError: null,

  basicSettingsUpdateSuccess: false,

  invoiceTemplateUpdating: false,

  invoiceTemplateUpdateError: null,

  invoiceTemplateUpdateSuccess: false,

  invoiceBrandingUpdating: false,

  invoiceBrandingUpdateError: null,

  invoiceBrandingUpdateSuccess: false,
};

/*
|--------------------------------------------------------------------------
| SLICE
|--------------------------------------------------------------------------
*/

const tenantProfileSlice = createSlice({
  name: "tenantProfile",

  initialState,

  reducers: {
    clearTenantProfile: (state) => {
      state.user = null;

      state.role = null;

      state.tenant = null;

      state.permissions = [];

      state.permissionDetails = [];

      state.loading = false;

      state.initialized = false;

      state.error = null;

      state.lastFetchedAt = null;
    },

    /*
      |--------------------------------------------------------------------------
      | CLEAR ERROR
      |--------------------------------------------------------------------------
      */

    clearTenantProfileError: (state) => {
      state.error = null;
    },

    /*
      |--------------------------------------------------------------------------
      | SET TENANT PROFILE
      |--------------------------------------------------------------------------
      |
      | Useful if tenant login already returns profile data.
      |--------------------------------------------------------------------------
      */

    setTenantProfile: (state, action) => {
      const payload = action.payload || {};

      state.user = payload?.user || null;

      state.role = payload?.role || null;

      state.tenant = payload?.tenant || null;

      state.permissions = Array.isArray(payload?.permissions)
        ? [
            ...new Set(
              payload.permissions.map(normalizePermission).filter(Boolean),
            ),
          ]
        : [];

      state.permissionDetails = Array.isArray(payload?.permissionDetails)
        ? payload.permissionDetails
        : [];

      state.initialized = true;

      state.error = null;

      state.lastFetchedAt = new Date().toISOString();
    },
  },

  /*
    |--------------------------------------------------------------------------
    | EXTRA REDUCERS
    |--------------------------------------------------------------------------
    */

  extraReducers: (builder) => {
    /*
|--------------------------------------------------------------------------
| UPDATE PROFILE - PENDING
|--------------------------------------------------------------------------
*/

    builder
      .addCase(
        updateTenantBasicSettings.pending,

        (state) => {
          state.basicSettingsUpdating = true;

          state.basicSettingsUpdateError = null;

          state.basicSettingsUpdateSuccess = false;
        },
      )

      .addCase(
        updateTenantBasicSettings.fulfilled,

        (state, action) => {
          state.basicSettingsUpdating = false;

          state.basicSettingsUpdateError = null;

          state.basicSettingsUpdateSuccess = true;

          if (action.payload?.settings && state.tenant) {
            state.tenant.settings = {
              ...state.tenant.settings,

              ...action.payload.settings,
            };
          }
        },
      )

      .addCase(
        updateTenantBasicSettings.rejected,

        (state, action) => {
          state.basicSettingsUpdating = false;

          state.basicSettingsUpdateSuccess = false;

          state.basicSettingsUpdateError = action.payload || {
            message:
              action.error?.message || "Unable to update basic settings.",
          };
        },
      )
      .addCase(
        updateTenantInvoiceTemplate.pending,

        (state) => {
          state.invoiceTemplateUpdating =
            true;

          state.invoiceTemplateUpdateError =
            null;

          state.invoiceTemplateUpdateSuccess =
            false;
        }
      )

      .addCase(
        updateTenantInvoiceTemplate.fulfilled,

        (
          state,
          action
        ) => {
          state.invoiceTemplateUpdating =
            false;

          state.invoiceTemplateUpdateError =
            null;

          state.invoiceTemplateUpdateSuccess =
            true;

          if (
            state.tenant
          ) {
            state.tenant.settings = {
              ...(
                state.tenant.settings ||
                {}
              ),

              invoiceTemplate:
                action.payload
                  ?.invoiceTemplate ||
                action.payload
                  ?.settings
                  ?.invoiceTemplate ||
                "MODERN_GST",
            };
          }
        }
      )

      .addCase(
        updateTenantInvoiceTemplate.rejected,

        (
          state,
          action
        ) => {
          state.invoiceTemplateUpdating =
            false;

          state.invoiceTemplateUpdateSuccess =
            false;

          state.invoiceTemplateUpdateError =
            action.payload || {
              message:
                action.error
                  ?.message ||
                "Unable to update default invoice template.",
            };
        }
      )

      .addCase(
        updateTenantInvoiceBranding.pending,

        (state) => {

          state.invoiceBrandingUpdating =
            true;

          state.invoiceBrandingUpdateError =
            null;

          state.invoiceBrandingUpdateSuccess =
            false;

        }
      )

      .addCase(
        updateTenantInvoiceBranding.fulfilled,

        (
          state,
          action
        ) => {

          state.invoiceBrandingUpdating =
            false;

          state.invoiceBrandingUpdateError =
            null;

          state.invoiceBrandingUpdateSuccess =
            true;


          if (
            state.tenant
          ) {

            state.tenant.invoiceBranding =
              action.payload
                ?.invoiceBranding ||
              {};

          }

        }
      )

      .addCase(
        updateTenantInvoiceBranding.rejected,

        (
          state,
          action
        ) => {

          state.invoiceBrandingUpdating =
            false;

          state.invoiceBrandingUpdateSuccess =
            false;

          state.invoiceBrandingUpdateError =
            action.payload ||
            {
              message:
                action.error
                  ?.message ||
                "Unable to update invoice branding.",
            };

        }
      )

      .addCase(
        changeTenantPassword.pending,

        (state) => {
          state.passwordUpdating = true;

          state.passwordUpdateError = null;

          state.passwordUpdateSuccess = false;
        },
      )

      .addCase(
        changeTenantPassword.fulfilled,

        (state) => {
          state.passwordUpdating = false;

          state.passwordUpdateError = null;

          state.passwordUpdateSuccess = true;
        },
      )

      .addCase(
        changeTenantPassword.rejected,

        (state, action) => {
          state.passwordUpdating = false;

          state.passwordUpdateSuccess = false;

          state.passwordUpdateError = action.payload || {
            message: action.error?.message || "Unable to change password.",
          };
        },
      )
      .addCase(
        updateTenantBusinessProfile.pending,

        (state) => {
          state.businessUpdating = true;

          state.businessUpdateError = null;

          state.businessUpdateSuccess = false;
        },
      )

      .addCase(
        updateTenantBusinessProfile.fulfilled,

        (state, action) => {
          state.businessUpdating = false;

          state.businessUpdateError = null;

          state.businessUpdateSuccess = true;

          if (action.payload?.tenant) {
            state.tenant = {
              ...state.tenant,

              ...action.payload.tenant,
            };
          }
        },
      )

      .addCase(
        updateTenantBusinessProfile.rejected,

        (state, action) => {
          state.businessUpdating = false;

          state.businessUpdateSuccess = false;

          state.businessUpdateError = action.payload || {
            message:
              action.error?.message || "Unable to update business details.",
          };
        },
      )
      .addCase(
        updateTenantProfile.pending,

        (state) => {
          state.updating = true;

          state.updateError = null;

          state.updateSuccess = false;
        },
      )

      /*
  |--------------------------------------------------------------------------
  | UPDATE PROFILE - SUCCESS
  |--------------------------------------------------------------------------
  */

      .addCase(
        updateTenantProfile.fulfilled,

        (state, action) => {
          state.updating = false;

          state.updateError = null;

          state.updateSuccess = true;

          /*
      |--------------------------------------------------------------------------
      | UPDATE USER IN EXISTING PROFILE STATE
      |--------------------------------------------------------------------------
      */

          if (action.payload?.user) {
            state.user = {
              ...state.user,

              ...action.payload.user,
            };
          }

          state.lastFetchedAt = new Date().toISOString();
        },
      )

      /*
  |--------------------------------------------------------------------------
  | UPDATE PROFILE - FAILED
  |--------------------------------------------------------------------------
  */

      .addCase(
        updateTenantProfile.rejected,

        (state, action) => {
          state.updating = false;

          state.updateSuccess = false;

          state.updateError = action.payload || {
            message: action.error?.message || "Unable to update profile.",
          };
        },
      );
    builder

      /*
        |--------------------------------------------------------------------------
        | FETCH PROFILE - PENDING
        |--------------------------------------------------------------------------
        */

      .addCase(fetchTenantProfile.pending, (state) => {
        state.loading = true;

        state.error = null;
      })

      /*
        |--------------------------------------------------------------------------
        | FETCH PROFILE - SUCCESS
        |--------------------------------------------------------------------------
        */

      .addCase(fetchTenantProfile.fulfilled, (state, action) => {
        state.loading = false;

        state.initialized = true;

        state.error = null;

        state.user = action.payload?.user || null;

        state.role = action.payload?.role || null;

        state.tenant = action.payload?.tenant || null;

        state.permissions = Array.isArray(action.payload?.permissions)
          ? action.payload.permissions
          : [];

        state.permissionDetails = Array.isArray(
          action.payload?.permissionDetails,
        )
          ? action.payload.permissionDetails
          : [];

        state.lastFetchedAt = new Date().toISOString();
      })

      /*
        |--------------------------------------------------------------------------
        | FETCH PROFILE - FAILED
        |--------------------------------------------------------------------------
        */

      .addCase(fetchTenantProfile.rejected, (state, action) => {
        state.loading = false;

        state.initialized = true;

        state.error = action.payload || {
          message: action.error?.message || "Unable to load business profile.",
        };

        /*
            |--------------------------------------------------------------------------
            | Do Not Keep Old Permission Data On Auth Failure
            |--------------------------------------------------------------------------
            */

        const statusCode = action.payload?.statusCode;

        if (statusCode === 401 || statusCode === 403) {
          state.user = null;

          state.role = null;

          state.tenant = null;

          state.permissions = [];

          state.permissionDetails = [];
        }
      });
  },
});

/*
|--------------------------------------------------------------------------
| ACTIONS
|--------------------------------------------------------------------------
*/

export const { clearTenantProfile, clearTenantProfileError, setTenantProfile } =
  tenantProfileSlice.actions;

/*
|--------------------------------------------------------------------------
| BASE SELECTORS
|--------------------------------------------------------------------------
*/

export const selectTenantProfileState = (state) => state.tenantProfile;

export const selectTenantUser = (state) => state.tenantProfile?.user || null;

export const selectTenantRole = (state) => state.tenantProfile?.role || null;

export const selectTenant = (state) => state.tenantProfile?.tenant || null;

export const selectTenantPermissions = (state) =>
  state.tenantProfile?.permissions || [];

export const selectTenantProfileLoading = (state) =>
  Boolean(state.tenantProfile?.loading);

export const selectTenantProfileInitialized = (state) =>
  Boolean(state.tenantProfile?.initialized);

export const selectTenantProfileError = (state) =>
  state.tenantProfile?.error || null;

/*
|--------------------------------------------------------------------------
| OWNER SELECTOR
|--------------------------------------------------------------------------
*/

export const selectIsTenantOwner = (state) => {
  return Boolean(state.tenantProfile?.user?.isOwner);
};

/*
|--------------------------------------------------------------------------
| ROLE SELECTORS
|--------------------------------------------------------------------------
*/

export const selectTenantRoleCode = (state) =>
  String(state.tenantProfile?.role?.code || "")
    .trim()
    .toUpperCase();

export const selectTenantUserType = (state) =>
  String(state.tenantProfile?.user?.userType || "")
    .trim()
    .toUpperCase();

/*
|--------------------------------------------------------------------------
| HAS PERMISSION
|--------------------------------------------------------------------------
|
| Owner gets unrestricted frontend access because backend already applies
| owner bypass.
|--------------------------------------------------------------------------
*/

export const hasTenantPermission = (state, permissionCode) => {
  const permission = normalizePermission(permissionCode);

  if (!permission) {
    return false;
  }

  /*
  |--------------------------------------------------------------------------
  | Owner
  |--------------------------------------------------------------------------
  */

  if (state.tenantProfile?.user?.isOwner) {
    return true;
  }

  /*
  |--------------------------------------------------------------------------
  | Normal User
  |--------------------------------------------------------------------------
  */

  const permissions = state.tenantProfile?.permissions || [];

  return permissions.includes(permission);
};

/*
|--------------------------------------------------------------------------
| HAS ANY PERMISSION
|--------------------------------------------------------------------------
*/

export const hasAnyTenantPermission = (state, permissionCodes = []) => {
  if (state.tenantProfile?.user?.isOwner) {
    return true;
  }

  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
    return false;
  }

  const permissions = state.tenantProfile?.permissions || [];

  return permissionCodes
    .map(normalizePermission)
    .filter(Boolean)
    .some((permission) => permissions.includes(permission));
};

/*
|--------------------------------------------------------------------------
| HAS ALL PERMISSIONS
|--------------------------------------------------------------------------
*/

export const hasAllTenantPermissions = (state, permissionCodes = []) => {
  if (state.tenantProfile?.user?.isOwner) {
    return true;
  }

  if (!Array.isArray(permissionCodes) || permissionCodes.length === 0) {
    return false;
  }

  const permissions = state.tenantProfile?.permissions || [];

  return permissionCodes
    .map(normalizePermission)
    .filter(Boolean)
    .every((permission) => permissions.includes(permission));
};

/*
|--------------------------------------------------------------------------
| FEATURE SELECTORS
|--------------------------------------------------------------------------
*/

export const selectTenantBranding = (state) =>
  state.tenantProfile?.tenant?.branding || {};

export const selectTenantSubscriptionStatus = (state) =>
  state.tenantProfile?.tenant?.subscriptionStatus || "";

export const selectStorefrontEnabled = (state) =>
  Boolean(state.tenantProfile?.tenant?.storefrontEnabled);

export const selectMarketplaceEnabled = (state) =>
  Boolean(state.tenantProfile?.tenant?.marketplaceEnabled);

export const selectTenantProfileUpdating = (state) =>
  Boolean(state.tenantProfile?.updating);

export const selectTenantProfileUpdateError = (state) =>
  state.tenantProfile?.updateError || null;

export const selectTenantProfileUpdateSuccess = (state) =>
  Boolean(state.tenantProfile?.updateSuccess);

export const selectTenantBusinessUpdating = (state) =>
  Boolean(state.tenantProfile?.businessUpdating);

export const selectTenantBusinessUpdateError = (state) =>
  state.tenantProfile?.businessUpdateError || null;

export const selectTenantBusinessUpdateSuccess = (state) =>
  Boolean(state.tenantProfile?.businessUpdateSuccess);

export const selectTenantPasswordUpdating = (state) =>
  Boolean(state.tenantProfile?.passwordUpdating);

export const selectTenantPasswordUpdateError = (state) =>
  state.tenantProfile?.passwordUpdateError || null;

export const selectTenantPasswordUpdateSuccess = (state) =>
  Boolean(state.tenantProfile?.passwordUpdateSuccess);

export const selectTenantBasicSettingsUpdating = (state) =>
  Boolean(state.tenantProfile?.basicSettingsUpdating);

export const selectTenantBasicSettingsUpdateError = (state) =>
  state.tenantProfile?.basicSettingsUpdateError || null;

export const selectTenantBasicSettingsUpdateSuccess = (state) =>
  Boolean(state.tenantProfile?.basicSettingsUpdateSuccess);

export const selectTenantInvoiceBranding = (
  state
) =>
  state.tenantProfile
    ?.tenant
    ?.invoiceBranding ||
  {
    logo: "",
    signatureMode: "NONE",
    signatureImage: "",
    signatureText: "",
    signatureStyle: "STYLE_1",
  };

export const selectTenantInvoiceBrandingUpdating = (
  state
) =>
  Boolean(
    state.tenantProfile
      ?.invoiceBrandingUpdating
  );

export const selectTenantInvoiceBrandingUpdateError = (
  state
) =>
  state.tenantProfile
    ?.invoiceBrandingUpdateError ||
  null;

export const selectTenantInvoiceBrandingUpdateSuccess = (
  state
) =>
  Boolean(
    state.tenantProfile
      ?.invoiceBrandingUpdateSuccess
  );


export const selectTenantInvoiceTemplate = (
  state
) =>
  state.tenantProfile
    ?.tenant
    ?.settings
    ?.invoiceTemplate ||
  "MODERN_GST";

export const selectTenantInvoiceTemplateUpdating = (
  state
) =>
  Boolean(
    state.tenantProfile
      ?.invoiceTemplateUpdating
  );

export const selectTenantInvoiceTemplateUpdateError = (
  state
) =>
  state.tenantProfile
    ?.invoiceTemplateUpdateError ||
  null;

export const selectTenantInvoiceTemplateUpdateSuccess = (
  state
) =>
  Boolean(
    state.tenantProfile
      ?.invoiceTemplateUpdateSuccess
  );

export const selectTenantPermissionDetails = (state) =>
  state.tenantProfile?.permissionDetails || [];
/*
|--------------------------------------------------------------------------
| REDUCER
|--------------------------------------------------------------------------
*/

export default tenantProfileSlice.reducer;


/* Employee-domain record scope. This does not alter Billing/Task/Product/Customer access. */
export const selectEmployeeDataScope = (state) => {
  const profile = state?.tenantProfile || {};
  if (profile?.user?.isOwner) return "ALL";
  return profile?.role?.dataScope || "SELF";
};
